import { NgTemplateOutlet } from '@angular/common';
import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  contentChild,
  ElementRef,
  input,
  signal,
  viewChild,
} from '@angular/core';
import type { IconName as FormaIconName } from '../icon/icon.types';
import { Card, type CardAppearance } from '../card/card';
import { Icon } from '../icon/icon';
import { type IconSize } from '../icon/icon.types';
import { FormaIllustration } from '../illustration/illustration';
import {
  IllustrationAnimation,
  type IllustrationColorScheme,
  type IllustrationSize,
  type IllustrationVariant,
} from '../illustration/illustration.types';
import {
  TextIconCardActions,
  TextIconCardAside,
  TextIconCardBody,
  TextIconCardHeading,
  TextIconCardIcon,
} from './text-icon-card-slots';

export interface TextIconCardIllustrationConfig {
  variant?: IllustrationVariant;
  colorScheme?: IllustrationColorScheme;
  size?: IllustrationSize;
  animation?: IllustrationAnimation | undefined;
  animationIteration?: number | undefined;
}

const DEFAULT_ILLUSTRATION_CONFIG = {
  variant: 'transparent',
  colorScheme: 'azur',
  size: 'large',
} satisfies TextIconCardIllustrationConfig;

export type TextIconCardImagePosition = 'end' | 'start';
export type TextIconCardLayout = 'default' | 'compact';

export type TextIconCardIllustrationPlacement = 'corner' | 'inline';

@Component({
  selector: 'forma-text-icon-card',
  templateUrl: './text-icon-card.html',
  styleUrls: ['./text-icon-card.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Card, Icon, FormaIllustration, NgTemplateOutlet],
  host: {
    '[class.image-start]': 'imagePosition() === "start"',
    '[class.compact]': 'layout() === "compact"',
  },
})
export class TextIconCard {
  /** Raw HTML content (supports CMS-sourced headings, spans, etc.) */
  readonly htmlContent = input<string>('');

  /** Plain-text heading rendered as <h1>. When set, takes precedence over htmlContent. */
  readonly heading = input<string | null>(null);

  /** Plain-text body rendered as <p>. When set, takes precedence over htmlContent. */
  readonly body = input<string | null>(null);

  /** Image URL to display beside the text */
  readonly imageSrc = input<string | null>(null);

  /** Alt text for the image (accessibility) */
  readonly imageAlt = input('');

  /** Max height of the image area */
  readonly imageMaxHeight = input<string>('100%');

  /** Phosphor icon name (e.g. "users", "chart-bar"). Renders <icon> when set. */
  readonly iconName = input<FormaIconName | null>(null);

  /** Size of the icon (named size or number in px). Sets both width and height equally. */
  readonly iconSize = input<IconSize>('large');

  /** Custom icon height (e.g. '48px', '3rem'). Overrides iconSize for height. */
  readonly iconHeight = input<string | null>(null);

  /** Custom icon width (e.g. '48px', '3rem'). Overrides iconSize for width. Defaults to iconHeight when only height is set. */
  readonly iconWidth = input<string | null>(null);

  /** Color of the icon */
  readonly iconColor = input<string>('var(--forma-brand-pine-green, #006e6d)');

  /** Card appearance variant passed through to forma-card */
  readonly cardAppearance = input<CardAppearance>('default');

  /** Background color for the card (any valid CSS color value) */
  readonly backgroundColor = input<string | null>(null);

  /** Controls whether the image appears before or after the text */
  readonly imagePosition = input<TextIconCardImagePosition>('end');

  /** Controls the card typography layout. */
  readonly layout = input<TextIconCardLayout>('default');

  /** Illustration name to display (e.g. 'symmetry', 'heart'). Takes precedence over imageSrc and iconName. */
  readonly illustration = input<string | null>(null);

  /** Configuration for the illustration (variant, colorScheme, size). Defaults to plain/azur/medium. */
  readonly illustrationConfig = input<TextIconCardIllustrationConfig>({});

  /** 'inline' places the illustration in the flex row, vertically centered; 'corner' overlays it at the card's top-right. */
  readonly illustrationPlacement = input<TextIconCardIllustrationPlacement>('inline');

  protected get resolvedIllustrationConfig() {
    return {
      ...DEFAULT_ILLUSTRATION_CONFIG,
      ...this.illustrationConfig(),
    } satisfies TextIconCardIllustrationConfig;
  }

  protected readonly textContent = viewChild.required<ElementRef<HTMLElement>>('textContent');
  protected readonly inlineIllustrationSize = signal(0);

  constructor() {
    afterRenderEffect((onCleanup) => {
      if (typeof ResizeObserver === 'undefined') return;

      const observer = new ResizeObserver(([entry]) => {
        this.inlineIllustrationSize.set(Math.round(entry.contentRect.height));
      });
      observer.observe(this.textContent().nativeElement);
      onCleanup(() => observer.disconnect());
    });
  }

  protected get resolvedIllustrationSize(): IllustrationSize {
    const configuredSize = this.illustrationConfig().size;
    if (configuredSize === undefined && this.illustrationPlacement() === 'inline') {
      return this.inlineIllustrationSize();
    }

    return this.resolvedIllustrationConfig.size;
  }

  /** Optional projected template for the heading. Takes precedence over `heading` and `htmlContent`. */
  protected readonly headingTpl = contentChild(TextIconCardHeading);

  /** Optional projected template for the body. Takes precedence over `body` and `htmlContent`. */
  protected readonly bodyTpl = contentChild(TextIconCardBody);

  /** Optional projected template for actions rendered beneath the text content. */
  protected readonly actionsTpl = contentChild(TextIconCardActions);

  /** Optional projected template for the icon/image slot. Takes precedence over `imageSrc` and `iconName`. */
  protected readonly iconTpl = contentChild(TextIconCardIcon);

  /** Optional projected template rendered in a second column next to the text content. */
  protected readonly asideTpl = contentChild(TextIconCardAside);
}
